import { useState, useEffect } from "react";
import "./createSales.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import SearchableSelect from "../components/SearchableSelect";
import { useToast } from "../context/ToastContext";
import SalesPersonModal from "../components/SalesPersonModal";

function CreateSales() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchSalesPersons = async () => {
    try {
      const res = await dataService.getSalesPersons();
      setSalesPersons(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      console.error("Failed to fetch sales persons", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, prodRes] = await Promise.all([
          dataService.getCustomers(),
          dataService.getProducts()
        ]);
        setCustomers(Array.isArray(custRes.data.data) ? custRes.data.data : []);
        setProducts(Array.isArray(prodRes.data.data) ? prodRes.data.data : []);
        await fetchSalesPersons();
      } catch (err) {
        console.error("Failed to fetch customers/products", err);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({
    orderId: `SO-${Math.floor(1000 + Math.random() * 9000)}`,
    soNumber: "",
    customer: "",
    date: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: "",
    shippingAddress: "",
    shippingCost: 0,
    shippingMethod: "standard method",
    status: "Draft",
    paymentStatus: "UnPaid",
    warehouse: "Main Warehouse - dubai",
    salesPerson: "john doe",
    customerNotes: "",
    internalNotes: ""
  });

  const [items, setItems] = useState([
    { product: "", quantity: 1, price: 0, discount: 0, tax: 5, total: 0 }
  ]);

  useEffect(() => {
    if (isEditMode) {
      const fetchOrder = async () => {
        try {
          const response = await dataService.getOrders("Sales Order");
          const order = response.data.data.find(o => o._id === id);
          if (order) {
            setFormData({
              orderId: order.orderId || "",
              soNumber: order.soNumber || "",
              customer: order.customer || "",
              date: order.date ? new Date(order.date).toISOString().split('T')[0] : "",
              expectedDeliveryDate: order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toISOString().split('T')[0] : "",
              shippingAddress: order.shippingAddress || "",
              shippingCost: order.shippingCost || 0,
              shippingMethod: order.shippingMethod || "standard method",
              status: order.status || "Draft",
              paymentStatus: order.paymentStatus || "UnPaid",
              warehouse: order.warehouse || "Main Warehouse - dubai",
              salesPerson: order.salesPerson || "john doe",
              customerNotes: order.customerNotes || "",
              internalNotes: order.internalNotes || ""
            });
            setItems(order.items || [{ product: "", quantity: 1, price: 0, discount: 0, tax: 5, total: 0 }]);
          } else {
            setError("Sales order not found");
          }
        } catch (err) {
          setError("Failed to fetch sales order details");
        } finally {
          // done
        }
      };
      fetchOrder();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: (name === "shippingCost") ? parseFloat(value) || 0 : value
      };

      // Auto-fill address if customer is selected
      if (name === "customer") {
        const selectedCust = customers.find(c => c.name === value);
        if (selectedCust) {
          updated.shippingAddress = selectedCust.address || "";
        }
      }
      return updated;
    });
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...items];

    if (name === "product") {
      newItems[index].product = value;
      const selectedProd = products.find(p => p.name === value);
      if (selectedProd) {
        newItems[index].price = selectedProd.retailPrice || 0;
        newItems[index].tax = 5; // User specifically asked for 5% tax
        newItems[index].imageUrl = selectedProd.imageUrl || "";
        // Auto-set warehouse from product's warehouse location
        if (selectedProd.warehouseLocation) {
          setFormData(prev => ({ ...prev, warehouse: selectedProd.warehouseLocation }));
        }
        // recalculate total
        const sub = (newItems[index].quantity * newItems[index].price) - (newItems[index].discount || 0);
        newItems[index].total = sub + (sub * newItems[index].tax / 100);
      }
    } else {
      newItems[index][name] = parseFloat(value) || 0;
      const qty = newItems[index].quantity;
      const price = newItems[index].price;
      const disc = newItems[index].discount;
      const tax = newItems[index].tax;
      const sub = (qty * price) - disc;
      newItems[index].total = sub + (sub * tax / 100);
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product: "", quantity: 1, price: 0, discount: 0, tax: 5, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0) + (parseFloat(formData.shippingCost) || 0);
  };

  const calculateTotalTax = () => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const disc = parseFloat(item.discount) || 0;
      const taxRate = parseFloat(item.tax) || 0;
      const sub = (qty * price) - disc;
      return sum + (sub * taxRate / 100);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const payload = {
      ...formData,
      type: "Sales Order",
      items,
      tax: calculateTotalTax(),
      amount: calculateGrandTotal()
    };

    try {
      if (isEditMode) {
        await dataService.updateOrder(id, payload);
        addToast("Sales order updated successfully", "success");
      } else {
        await dataService.addOrder(payload);
        addToast("Sales order created successfully", "success");
      }
      navigate("/sales-orders");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create sales order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Sales Order" : "Create Sales Order"}</h1>
        <p>{isEditMode ? `Updating ${formData.orderId}` : "Create a new sales order for customers"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Sales Order Details</h2>
              <div className="v-name">
                <div>
                  <p>Order Identification</p>
                  <input name="orderId" value={formData.orderId} onChange={handleChange} required placeholder="SO-xxx" />
                </div>
                <div>
                  <p>SO Number (Internal)</p>
                  <input name="soNumber" value={formData.soNumber} onChange={handleChange} placeholder="Ref-xxx" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <SearchableSelect
                    label="Customer Name/ID"
                    name="customer"
                    options={customers}
                    value={formData.customer}
                    onChange={handleChange}
                    placeholder="Search/Select a Customer"
                    addPath="/customers/add"
                    addLabel="+ New Customer"
                  />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Order Date</p>
                  <input type="date" name="date" value={formData.date} onChange={handleChange} required />
                </div>
                <div>
                  <p>Expected Delivery Date</p>
                  <input type="date" name="expectedDeliveryDate" value={formData.expectedDeliveryDate} onChange={handleChange} />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Shipping Address</p>
                  <input name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} placeholder="enter shipping address" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Order Items</h2>
              {items.map((item, index) => (
                <div key={index} className="v-name" style={{ marginBottom: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 2 }}>
                    <SearchableSelect
                      label="Product"
                      name="product"
                      options={products}
                      value={item.product}
                      onChange={(e) => handleItemChange(index, e)}
                      placeholder="Search Product"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Qty</p>
                    <input type="number" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(index, e)} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Unit Price</p>
                    <input type="number" name="price" value={item.price} onChange={(e) => handleItemChange(index, e)} required readOnly style={{ background: '#f3f4f6' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Disc (AED)</p>
                    <input type="number" name="discount" value={item.discount} onChange={(e) => handleItemChange(index, e)} readOnly style={{ background: '#f3f4f6' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Tax %</p>
                    <input type="number" name="tax" value={item.tax} onChange={(e) => handleItemChange(index, e)} readOnly style={{ background: '#f3f4f6' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Total</p>
                    <input value={item.total.toFixed(2)} disabled />
                  </div>
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(index)} style={{ padding: '8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>×</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addItem} className="secondary-btn" style={{ marginTop: '10px' }}>+ Add Item</button>
            </div>

            <div className="vendor-card">
              <h2>Additional Information</h2>
              <div className="v-name">
                <div>
                  <p>Shipping Cost</p>
                  <input type="number" name="shippingCost" value={formData.shippingCost} onChange={handleChange} />
                </div>
                <div>
                  <p>Shipping method</p>
                  <select name="shippingMethod" value={formData.shippingMethod} onChange={handleChange}>
                    <option value="standard method">standard method</option>
                    <option value="express method">express method</option>
                    <option value="overnight shipping">overnight shipping</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Order Summary</h2>
                <div>
                  <p>Grand Total</p>
                  <input value={`AED ${calculateGrandTotal().toFixed(2)}`} disabled style={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Order Settings</h2>
                <div>
                  <p>Order Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Draft">Draft</option>
                    <option value="Pending">Pending</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Processing">Processing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                  </select>
                  <p>Payment Status</p>
                  <select name="paymentStatus" value={formData.paymentStatus} onChange={handleChange}>
                    <option value="Paid">Paid</option>
                    <option value="UnPaid">UnPaid</option>
                    <option value="PartiallyPaid">PartiallyPaid</option>
                  </select>
                  {/* Warehouse hidden as requested - automated based on product creation */}
                  <select name="warehouse" value={formData.warehouse} onChange={handleChange} style={{ display: 'none' }}>
                    <option value="Main Warehouse - dubai">Main Warehouse - Dubai</option>
                    <option value="West Coast Hub - sharjah">West Coast Hub - Sharjah</option>
                  </select>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0 }}>Sales Person</p>
                    <button type="button" onClick={() => setIsModalOpen(true)} style={{
                      background: 'none', border: 'none', color: '#2563EB', fontSize: '11px',
                      fontWeight: '600', cursor: 'pointer', padding: '2px 4px'
                    }}>
                      Manage
                    </button>
                  </div>
                  <select name="salesPerson" value={formData.salesPerson} onChange={handleChange}>
                    <option value="">Select Sales Person</option>
                    {salesPersons.map(sp => (
                      <option key={sp._id} value={sp.name}>{sp.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <SalesPersonModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              salesPersons={salesPersons}
              onUpdate={fetchSalesPersons}
            />

            <div className="vendor-card">
              <h2>Notes</h2>
              <p>Customer Notes</p>
              <textarea name="customerNotes" value={formData.customerNotes} onChange={handleChange} placeholder="Visible to customer" rows={3} style={{ width: '100%', marginBottom: '10px' }}></textarea>
              <p>Internal Notes</p>
              <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} placeholder="Not visible to customer" rows={3} style={{ width: '100%' }}></textarea>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/sales-orders")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Sales Order" : "Create Sales Order")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateSales;
